<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import SectionTitle from '@/components/common/section-title.vue';
import { Marquee, ReviewCard } from '@/components/inspira/marquee';
import { TestimonialSlider } from '@/components/inspira/testimonial-slider';
import { AnimatedContent } from '@/components/vuebits';
import {
  getMarqueeTestimonials,
  getSliderTestimonials,
  getTestimonialsSection,
} from '@/config/testimonials';

const { locale } = useI18n();

const section = computed(() => getTestimonialsSection(locale.value));
const testimonials = computed(() => getSliderTestimonials(locale.value));
const marqueeReviews = computed(() => getMarqueeTestimonials(locale.value));
const marqueeFirstRow = computed(() => marqueeReviews.value.slice(0, 3));
const marqueeSecondRow = computed(() => marqueeReviews.value.slice(3));

/** 离开视口暂停跑马灯，减轻滚动合成压力 */
const marqueeRef = ref(null);
const marqueeInView = ref(false);
let marqueeObserver = null;

onMounted(() => {
  marqueeObserver = new IntersectionObserver(
    ([entry]) => {
      marqueeInView.value = entry?.isIntersecting ?? false;
    },
    { threshold: 0.05 },
  );
  if (marqueeRef.value) marqueeObserver.observe(marqueeRef.value);
});

onBeforeUnmount(() => {
  marqueeObserver?.disconnect();
  marqueeObserver = null;
});
</script>

<template>
  <section id="testimonials" class="relative overflow-hidden pb-0 pt-6 md:pt-8">
    <div class="section-shell relative">
      <AnimatedContent :distance="24">
        <p class="eyebrow text-center">{{ section.eyebrow }}</p>
      </AnimatedContent>
      <SectionTitle :title="section.title" />
      <AnimatedContent :delay="0.05" :distance="20" class-name="-mt-7">
        <p class="mx-auto max-w-2xl text-center text-[15px] leading-7 text-muted-foreground sm:text-base md:text-lg">
          {{ section.subtitle }}
        </p>
      </AnimatedContent>

      <AnimatedContent :delay="0.1" :distance="24" class-name="mt-8 md:mt-14">
        <TestimonialSlider :testimonials="testimonials" :duration="6" />
      </AnimatedContent>
    </div>

    <!-- 跑马灯：开放引言流，两侧淡出；离屏暂停 -->
    <AnimatedContent :delay="0.15" :distance="20" class-name="mt-10 md:mt-16">
      <div
        ref="marqueeRef"
        class="relative overflow-hidden border-y border-slate-200/80 py-5 md:py-8"
      >
        <div
          class="pointer-events-none absolute inset-y-0 left-0 z-10 w-10 bg-gradient-to-r from-[#f8fafc] to-transparent md:w-16 lg:w-28"
        />
        <div
          class="pointer-events-none absolute inset-y-0 right-0 z-10 w-10 bg-gradient-to-l from-[#f8fafc] to-transparent md:w-16 lg:w-28"
        />

        <Marquee
          :paused="!marqueeInView"
          pause-on-hover
          :repeat="4"
          class="[--duration:32s] [--gap:0px]"
        >
          <ReviewCard
            v-for="review in marqueeFirstRow"
            :key="review.name"
            v-bind="review"
          />
        </Marquee>

        <div class="my-3 h-px w-full bg-slate-200/60 md:my-6" />

        <Marquee
          reverse
          :paused="!marqueeInView"
          pause-on-hover
          :repeat="4"
          class="[--duration:32s] [--gap:0px]"
        >
          <ReviewCard
            v-for="review in marqueeSecondRow"
            :key="review.name"
            v-bind="review"
          />
        </Marquee>
      </div>
    </AnimatedContent>
  </section>
</template>
